<template>
  <transition-group
    ref="imageList"
    class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 md:tw-gap-x-2.5 lg:tw-grid-cols-3"
    enter-active-class="fade-enter-transition"
    enter-from-class="fade-out"
    leave-active-class="fade-leave-transition"
    leave-to-class="fade-out"
    tag="ul"
  >
    <li
      v-for="visibleImage in visibleImages"
      :key="visibleImage.image.id"
      class="tw-h-72 lg:tw-h-80 tw-mb-4"
    >
      <ImageViewCard
        :image="visibleImage.image"
        :image-type="imageType"
        :is-favorites="isFavorites"
        :is-full-height="true"
        :location="visibleImage.location"
        @show-image="showImage(visibleImage, 'image', visibleImages)"
        @show-video="showImage(visibleImage, 'video', visibleImages)"
      />
    </li>
  </transition-group>
</template>

<script>
import { waitForRef } from '@@/utils/Components/ComponentUtils';
import InfiniteScrollMixin from '@@/utils/Components/InfiniteScrollMixin';
import ShowImagesMixin, {
  getLocationName,
  getImageName,
  getImageUrl,
} from '@@/components/Location/Mixins/ShowImagesMixin';

export default {
  name: 'ImageViewCardList',

  mixins: [
    InfiniteScrollMixin,
    ShowImagesMixin,
  ],

  props: {
    images: {
      type: Array,
      required: true,
    },
    imageType: {
      type: String,
      required: true,
    },
    isFavorites: {
      type: Boolean,
      default: false,
    },
  },

  setup(props) {
    const { params, query } = useRoute();
    let imageId;

    if (query.id) {
      imageId = Number(query.id);
    }
    else if (params.id) {
      imageId = Number(params.id);
    }

    const requestedImage = props.images.find((i) => i.image.id === imageId);

    if (requestedImage) {
      const imageName = getImageName(requestedImage);
      const locationName = getLocationName(requestedImage);
      const title = `${locationName} - ${imageName} | OpenSnow`;
      const imageUrl = getImageUrl(requestedImage);

      // TODO: Is adding the itemprop attribute necessary? useSeoMeta() doesn't support it, but it
      // was present in OpenMountain-Web. For now it's been omitted.
      //
      // - Add itemprop="image primaryImageOfPage" attribute to ogImage
      // - Add itemprop="name" attribute to ogTitle

      useSeoMeta({
        ogImage: imageUrl,
        ogTitle: title,
        twitterImage: imageUrl,
        twitterTitle: title,
      });
    }

    return { requestedImage };
  },

  data() {
    return {
      limit: 15,
      skip: 0,
    };
  },

  computed: {
    visibleImages() {
      return this.images.slice(0, this.skip + this.limit);
    },
  },

  async mounted() {
    try {
      await waitForRef(this, 'imageList');
      this.addIntersectionObserver(this.$refs.imageList.$el, () => this.fetchMoreVisibleImages());
    }
    catch (e) {
      // Do nothing, only the first 15 images will be visible.
    }

    this.showRequestedImage();
  },

  methods: {
    fetchMoreVisibleImages() {
      return this.skip < this.images.length;
    },

    showRequestedImage() {
      if (!this.requestedImage) {
        return;
      }

      this.showImage(this.requestedImage, 'image', this.visibleImages);
    },
  },
};
</script>
